import { useState, useRef } from 'react';
import { Button, Icon, ComposeDropDown, Tooltip, Text } from '@sendible/design-system/src';
import { EVENTS, publish } from '@sendible/event-manager';
import { useMediaControls } from '@sendible/frontendv2/src/hooks/useMediaControls';
import { BackgroundFiller, Container, Details, Image, MediaBadge } from './index.styles';
import { PdfThumbnail } from '../../PdfThumbnail';
import { useGetValidationForMedia } from '../../../MediaValidations/useGetValidationForMedia';
import { useComposeBoxContext } from '../../../../composeBoxContext';
import { MediaValidations } from '../../../MediaValidations';

interface DropdownAction {
  action: string;
  click?: () => void;
  isHighlighted?: boolean;
  label: string;
}

export interface ComposeMediaCardProps extends Omit<Component, 'id'> {
  ariaMediaCardLabel?: string;
  dropdownButtonClick: (option: DropdownAction['action']) => void;
  isDropdownEnabled?: boolean;
  open?: (id: MediaIdType) => void;
  orderNumber?: string;
  tabIndex?: number;
  media: MediaType & MessageMediaType;
}

export const ComposeMediaCard = (props: ComposeMediaCardProps) => {
  const {
    ariaMediaCardLabel = '',
    className = '',
    customClass = '',
    dropdownButtonClick,
    isDropdownEnabled = true,
    media,
    open,
    orderNumber = '',
    tabIndex = -1,
    testId = 'composemediacard',
  } = props;
  const { activeComposeBoxTab, activePostType } = useComposeBoxContext();
  const { getMainValidationType } = useGetValidationForMedia();
  const [isThumbReady, setIsThumbReady] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const actionsButtonRef = useRef(null);
  const mediaTypeLowerCase = media.type.toLowerCase();
  const { dropdownActions } = useMediaControls(activeComposeBoxTab);
  const validationErrorType = getMainValidationType(media, activeComposeBoxTab, activePostType);
  const isGif = media.metadata && media.metadata?.contentType?.includes('gif');
  const TAG = 'tag';
  const dropdownKey = isGif ? 'gif' : 'image';
  const hasTag = !!dropdownActions[dropdownKey].find((item) => item.action === TAG) && !!media.igUserTags?.length;

  if (hasTag) {
    const tagOption = dropdownActions[dropdownKey].find((option) => option.action === TAG);

    if (tagOption) {
      tagOption.altLabel = media.igUserTags?.length.toString() || '0';
    }
  }

  const getTime = () => {
    const totalSeconds = Math.floor((media.metadata?.duration || 0) / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;

    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const icon = mediaTypeLowerCase.includes('image') ? 'image' : mediaTypeLowerCase.includes('video') ? 'video' : 'file';

  const handleAltTextClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    publish(EVENTS.OPEN_ALT_TEXT_MODAL, media);
  };

  return (
    <Container
      className={`mediacard ${customClass} ${className}`}
      data-testid={testId}
      id={media.name}
      $validation={validationErrorType}
    >
      <Image
        aria-label={ariaMediaCardLabel}
        data-testid={`${testId}-image`}
        tabIndex={tabIndex}
        $validation={validationErrorType}
      >
        <>
          <BackgroundFiller
            data-testid={`${testId}-button`}
            id={media.id.toString()}
            onClick={() => open && open(media.id)}
            tabIndex={-1}
          />

          {media.thumbnailUrl && (
            <img
              alt={media.altText || ''}
              src={media.thumbnailUrl}
              data-testid={media.name}
              onLoad={() => setIsThumbReady(true)}
            />
          )}

          {mediaTypeLowerCase.includes('document') && (
            <PdfThumbnail
              url={media.originalUrl}
              altText={media.altText || 'PDF document'}
            />
          )}

          {!isThumbReady && (
            <Icon
              customClass="placeholder"
              name={icon}
            />
          )}

          {mediaTypeLowerCase.includes('image') && isGif && (
            <MediaBadge
              position="bottom_left"
              data-testid="gif-label"
            >
              GIF
            </MediaBadge>
          )}

          {mediaTypeLowerCase.includes('image') && hasTag && (
            <MediaBadge
              as="button"
              type="button"
              position="bottom_right"
              isIconHolder
              aria-label={TAG}
              onClick={(e) => {
                e.stopPropagation();
                dropdownButtonClick(TAG);
              }}
              data-testid="hastag-label"
            >
              <Icon
                name="tag"
                color="brandLight"
              />
            </MediaBadge>
          )}

          {mediaTypeLowerCase.includes('video') && (media.metadata?.duration || 0) && (
            <MediaBadge
              position="bottom_left"
              data-testid="video-duration-label"
            >
              {getTime()}
            </MediaBadge>
          )}
          {mediaTypeLowerCase.includes('document') && (
            <MediaBadge
              position="bottom_left"
              data-testid="pdf-label"
            >
              PDF
            </MediaBadge>
          )}
          {media.altText && (
            <MediaBadge
              position={isGif ? 'bottom_middle' : 'bottom_left'}
              onClick={handleAltTextClick}
              data-testid="alt-label"
            >
              ALT
            </MediaBadge>
          )}
          {orderNumber ? (
            <MediaBadge
              position="top_left"
              data-testid="order-number"
            >
              {orderNumber}
            </MediaBadge>
          ) : (
            ''
          )}
        </>
      </Image>
      <Details>
        <Text
          color="brandDark"
          variation="body_12"
          data-testid={`${testId}-filename`}
        >
          {media.fileDisplayName || media.name}
        </Text>
      </Details>
      {isDropdownEnabled && (
        <ComposeDropDown
          renderHeader={() => (
            <MediaValidations
              media={media}
              tabId={activeComposeBoxTab}
              postType={activePostType}
            />
          )}
          options={isGif ? dropdownActions.gif : dropdownActions[mediaTypeLowerCase] || []}
          placement="bottom-start"
          select={(option: string) => dropdownButtonClick(option)}
          visibilityChange={(state: boolean) => !state && setIsDropdownOpen(false)}
        >
          <Tooltip
            defaultPosition="top"
            content={validationErrorType ? <>See details</> : <>Options</>}
          >
            <Button
              aria-controls="menu"
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
              customClass={isDropdownOpen ? 'show-dropdown-button' : ''}
              data-testid={`${testId}-dropdown-btn`}
              icon={validationErrorType || 'chevron_down'}
              onClick={() => {
                setIsDropdownOpen(isDropdownOpen);
              }}
              ref={actionsButtonRef}
              size={12}
              appearance={validationErrorType || 'default'}
              variation="fill"
            />
          </Tooltip>
        </ComposeDropDown>
      )}
    </Container>
  );
};
